import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, toast, ToastBar } from 'react-hot-toast';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider, useAuthContext } from './features/auth/context/AuthContext';
import { ThemeProvider, useTheme } from './features/theme/context/ThemeContext';
import { usePWADisplay } from './features/pwa/hooks/usePWADisplay';
import { AppRoutes } from './routes/AppRoutes';
import PWAPrompt from './components/common/PWAPrompt';
import ErrorBoundary from './components/common/ErrorBoundary';
import OfflineScreen from './components/common/OfflineScreen';
import DomainError from './components/DomainError';
import InfoModalProvider from './components/common/InfoModalProvider';
import GlobalFeedbackButton from './components/common/GlobalFeedbackButton';
import SystemStatusScreen from './components/common/SystemStatusScreen';
import { useSystemStatus } from './hooks/useSystemStatus';
import { useAxiosInterceptor } from './hooks/useAxiosInterceptor';
import { isPublicStatusBrowseContext, isPublicStatusPageHost } from './utils/publicStatusPage';
/* After other App imports (routes, layout) so scaled radii + corner-shape reset win the cascade */
import './styles/safari-squircle-fallback.css';

const StatusPage = lazy(() => import('./components/StatusPage'));
const UnsubscribePage = lazy(() => import('./components/UnsubscribePage'));
const ManageSubscriptionPage = lazy(() => import('./components/ManageSubscriptionPage'));

/**
 * Standalone shell for the public status subdomain (e.g. status.midus.co).
 * Renders only ThemeProvider + BrowserRouter — no AuthProvider, no interceptors,
 * no domain validation, no system-status overlay.
 */
const StatusSiteShell = () => (
  <ThemeProvider>
    <BrowserRouter>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen" />}>
        <Routes>
          <Route path="/" element={<StatusPage />} />
          <Route path="/manage" element={<ManageSubscriptionPage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ThemeProvider>
);

// Inner app component that has access to auth context
const AppContent = () => {
  const { user } = useAuthContext();
  const { darkMode } = useTheme();
  
  // Set up global axios interceptor for auth error handling
  useAxiosInterceptor();
  const { 
    systemStatus, 
    isLoading: statusLoading, 
    shouldBypassStatus, 
    refreshStatus,
    isSystemDown 
  } = useSystemStatus();

  // Check if we're on an admin route or status route
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  const isStatusRoute = isPublicStatusBrowseContext();

  // Check if user should see the system status page
  // Don't show status page if:
  // 1. User is on admin routes (admins need access to manage status)
  // 2. User is on the public status site (users need to see the status page during maintenance)
  // 3. User should bypass the status (admin permissions)
  // 4. System is not down
  const showStatusPage = isSystemDown && !isAdminRoute && !isStatusRoute && !shouldBypassStatus(user);

  // Show system status screen if active and user shouldn't bypass
  if (showStatusPage && !statusLoading) {
    return <SystemStatusScreen status={systemStatus} onRetry={refreshStatus} />;
  }

  return (
    <>
      <DomainError />
      <Toaster
        position="top-center"
        containerStyle={{ zIndex: 999999 }}
        toastOptions={{
          style: {
            padding: 0,
            background: 'transparent',
            boxShadow: 'none',
            maxWidth: 'none',
          },
          success: { style: { padding: 0, background: 'transparent', boxShadow: 'none', maxWidth: 'none' } },
          error: { style: { padding: 0, background: 'transparent', boxShadow: 'none', maxWidth: 'none' } },
          loading: { style: { padding: 0, background: 'transparent', boxShadow: 'none', maxWidth: 'none' } },
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => {
              const isSuccess = t.type === 'success';
              const isError = t.type === 'error';
              const isLoading = t.type === 'loading';
              const bg = darkMode
                ? (isSuccess ? '#064e3b' : isError ? '#451a1a' : isLoading ? '#252525' : '#1a1a1a')
                : (isSuccess ? '#ecfdf5' : isError ? '#fef2f2' : isLoading ? '#ebebeb' : '#fafafa');
              const color = isSuccess ? (darkMode ? '#6ee7b7' : '#065f46') : isError ? (darkMode ? '#fca5a5' : '#b91c1c') : isLoading ? (darkMode ? '#999999' : '#525252') : (darkMode ? '#ffffff' : '#000');
              const borderColor = darkMode
                ? (isSuccess ? '#047857' : isError ? '#991b1b' : isLoading ? '#333' : '#2a2a2a')
                : (isSuccess ? '#d1fae5' : isError ? '#fee2e2' : isLoading ? '#d4d4d4' : '#f3f4f6');
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    borderRadius: 12,
                    background: bg,
                    color,
                    fontSize: 13,
                    width: 'fit-content',
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  {icon}
                  <span style={{ width: 'fit-content' }}>{message}</span>
                </div>
              );
            }}
          </ToastBar>
        )}
      </Toaster>
      <BrowserRouter>
        <InfoModalProvider>
          <AppRoutes />
          <GlobalFeedbackButton />
        </InfoModalProvider>
      </BrowserRouter>
    </>
  );
};

function App() {
  const { isIOSSafari } = usePWADisplay();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const getDisablePWAPrompt = () => {
    if (typeof window === 'undefined') {
      return false;
    }

    if (sessionStorage.getItem('disablePWAPrompt') === 'true') {
      return true;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('disablePWAPrompt') === 'true') {
      sessionStorage.setItem('disablePWAPrompt', 'true');
      return true;
    }

    return false;
  };
  const [disablePWAPrompt, setDisablePWAPrompt] = useState(getDisablePWAPrompt);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateDisableState = () => {
      const params = new URLSearchParams(window.location.search);
      if (params.get('disablePWAPrompt') === 'true') {
        sessionStorage.setItem('disablePWAPrompt', 'true');
        setDisablePWAPrompt(true);
      } else if (!sessionStorage.getItem('disablePWAPrompt')) {
        setDisablePWAPrompt(false);
      }
    };

    updateDisableState();
    window.addEventListener('popstate', updateDisableState);

    return () => window.removeEventListener('popstate', updateDisableState);
  }, []);

  const isStatusHost = isPublicStatusPageHost();
  const isStatusRoute = isPublicStatusBrowseContext();

  // If running in iOS Safari, show the PWA prompt (except for status routes)
  if (isIOSSafari && !isStatusRoute && !disablePWAPrompt) {
    return <PWAPrompt />;
  }

  // If offline, show the offline screen
  if (!isOnline) {
    return <OfflineScreen />;
  }

  // Status subdomain gets a minimal shell — no auth, no interceptors.
  if (isStatusHost) {
    return (
      <ErrorBoundary>
        <StatusSiteShell />
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </AuthProvider>
      <Analytics
        beforeSend={(event) => {
          if (typeof window !== 'undefined' && localStorage.getItem('va-disable') === '1') {
            return null;
          }
          return event;
        }}
      />
    </ErrorBoundary>
  );
}

export default App;